import React, { useState } from 'react';
import type { Track, AudioFile } from '../services/trackTypes';
import { Link, useNavigate } from 'react-router-dom';
import { MusicalNoteIcon, PauseIcon, PlayIcon, UsersIcon, EllipsisHorizontalIcon, TrashIcon, UserMinusIcon, ShareIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import CassetteLoader from './CassetteLoader';
import { getStatusColor } from '../services/kanbanService';
import { useFloating, useInteractions, useRole, useDismiss, offset, flip, shift } from '@floating-ui/react';
import ShareInfoPopup from './ShareInfoPopup';
import { Menu, Transition } from '@headlessui/react';
import { useDesignMode } from '../contexts/DesignContext';
import { classNames } from '../utils/classNames';
import { sketchTiltStyle } from '../utils/sketchTilt';
import { SketchProgressBar } from './sketchy';

interface DragBindings {
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: any;
  dragHandleProps?: any;
}

export interface TrackTileProps extends DragBindings {
  track: Track;
  isAccessible: boolean;
  isEditable: boolean;
  t: (key: string, defaultValue?: any) => string;
  // Artwork
  artworkUrl?: string | null;
  artworkLoading?: boolean;
  onArtworkClick?: () => void;
  // Delete/Uncollaborate
  deleting?: boolean;
  uncollaborating?: boolean;
  onDelete?: () => void;
  onUncollaborate?: () => void;
  // Audio preview
  previewAudio?: AudioFile | undefined;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  // Sharing
  sharedRole?: 'viewer' | 'editor' | null;
  sharedCount?: number;
  isOwner?: boolean;
  ownerUserId?: string;
}

const Tooltip: React.FC<{ content: string; children: React.ReactElement }> = ({ content, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    middleware: [offset(8), flip(), shift()],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const { getFloatingProps } = useInteractions([
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  return (
    <>
      {React.cloneElement(children, { ref: refs.setReference, onMouseEnter: () => setIsOpen(true), onMouseLeave: () => setIsOpen(false) })}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg"
        >
          {content}
        </div>
      )}
    </>
  );
};

const TrackTile: React.FC<TrackTileProps> = ({
  track,
  isAccessible,
  isEditable,
  t,
  artworkUrl,
  artworkLoading,
  onArtworkClick,
  deleting,
  uncollaborating,
  onDelete,
  onUncollaborate,
  previewAudio,
  isPlaying,
  onTogglePlay,
  innerRef,
  draggableProps,
  sharedRole,
  sharedCount,
  isOwner = true,
  ownerUserId,
}) => {
  const colors = getStatusColor(track.status);
  const { isSketchy } = useDesignMode();
  const [showShareInfo, setShowShareInfo] = useState(false);
  const [isPlayButtonHovered, setIsPlayButtonHovered] = useState(false);
  const navigate = useNavigate();

  // Format release date
  const formatReleaseDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <div className="relative">
        <Link
          to={`/tracks/${track.id}`}
          className="block"
        >
          <div
            ref={innerRef as any}
            {...draggableProps}
            className={classNames(
              'group relative shadow-sm hover:shadow-xl transition-all duration-300 border w-full min-h-[15.5rem] px-5 py-4 flex flex-col gap-2.5',
              isSketchy ? 'sketch-box sketch-wobble rounded-sm' : 'rounded-[20px]',
              !isAccessible && 'opacity-60 pointer-events-none',
              (deleting || uncollaborating) && 'opacity-50 pointer-events-none',
              !isEditable && isOwner && 'opacity-60 grayscale-[0.6]'
            )}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              ...(isSketchy ? sketchTiltStyle(track.id) : {}),
            }}
            data-testid={`track-tile-${track.id}`}
          >
      {/* Delete/Uncollaborate Loading Overlay */}
      {(deleting || uncollaborating) && (
        <div className="absolute inset-0 flex items-center justify-center z-20 rounded-[20px]" style={{ backgroundColor: 'var(--overlay-bg)' }}>
          <div className="flex flex-col items-center gap-3">
            <CassetteLoader size={48} variant="inline" />
            <span className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>
              {deleting ? t('dashboard.deletingTrack') : t('dashboard.leaving')}
            </span>
          </div>
        </div>
      )}


      {/* Top Section - Artwork + status */}
      <div className="relative w-full pr-10">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 shrink-0 flex justify-center items-center">
            {artworkUrl ? (
              <img
                src={artworkUrl}
                alt={t('track.artwork')}
                className="w-full h-full object-cover rounded-xl cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  onArtworkClick && onArtworkClick();
                }}
              />
            ) : (
              <div className="relative w-full h-full">
                {artworkLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center z-20 rounded-xl" style={{ backgroundColor: 'var(--overlay-bg)' }}>
                    <CassetteLoader size={40} variant="inline" />
                  </div>
                ) : null}
                <div className="w-full h-full gradient-brand-br rounded-xl flex items-center justify-center">
                  <MusicalNoteIcon className="h-5 w-5 text-white" />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div
              className="inline-flex self-start px-2.5 py-1 rounded-full"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
              }}
            >
              <span className="text-xs font-medium leading-none">
                {t(`track.statusLabels.${track.status}`, track.status)}
              </span>
            </div>

            {(sharedRole || (sharedCount !== undefined && sharedCount > 0)) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setShowShareInfo(true);
                }}
                className="inline-flex self-start items-center gap-1 px-2.5 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: 'var(--collab-blue-bg)', color: 'var(--collab-blue-text)' }}
                title={t('share.clickForDetails')}
              >
                <UsersIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium leading-none truncate">
                  {sharedRole
                    ? (sharedRole === 'editor' ? t('share.canEdit') : t('share.canView'))
                    : t('share.sharedWith', { count: sharedCount })
                  }
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Track Title */}
      <div className="w-full min-w-0" style={{ color: 'var(--color-text)' }}>
        <Tooltip content={track.name}>
          <h3 className="text-sm font-semibold leading-snug line-clamp-2">
            {track.name}
          </h3>
        </Tooltip>
      </div>

      {/* Metadata + progress + play */}
      <div className="mt-auto w-full flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          {track.releaseDate && (
            <p className="text-xs leading-snug truncate" style={{ color: 'var(--color-muted)' }}>
              <span>{t('track.release')}:</span>
              <span style={{ color: 'var(--color-text)' }}> {formatReleaseDate(track.releaseDate)}</span>
            </p>
          )}

          {track.artist && (
            <p className="text-xs leading-snug truncate" style={{ color: 'var(--color-muted)' }}>
              <span>{t('track.artist')}:</span>
              <span style={{ color: 'var(--color-text)' }}> {track.artist}</span>
            </p>
          )}

          <div className="w-full">
            <div className="flex items-center justify-between text-xs leading-none mb-1">
              <span style={{ color: 'var(--color-muted)' }}>{t('track.progress')}</span>
              <span style={{ color: 'var(--color-text)' }}>{track.progress}%</span>
            </div>
            <SketchProgressBar value={track.progress} className="max-w-full" data-testid={`track-tile-progress-${track.id}`} />
          </div>
        </div>

        {previewAudio && (
          <div
            className="w-11 h-11 shrink-0 flex justify-center items-center border rounded-lg transition-colors"
            style={{
              borderColor: 'var(--brand-from)',
              backgroundColor: isPlayButtonHovered ? 'var(--brand-from)' : 'transparent',
            }}
            onMouseEnter={() => setIsPlayButtonHovered(true)}
            onMouseLeave={() => setIsPlayButtonHovered(false)}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                onTogglePlay && onTogglePlay();
              }}
              className="w-6 h-6 flex items-center justify-center"
              aria-label={isPlaying ? t('track.stop') : t('track.play')}
            >
              {isPlaying ? (
                <PauseIcon className="w-5 h-5" style={{ color: isPlayButtonHovered ? 'white' : 'var(--brand-from)' }} />
              ) : (
                <PlayIcon className="w-5 h-5 ml-0.5" style={{ color: isPlayButtonHovered ? 'white' : 'var(--brand-from)' }} />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Premium Lock Overlay */}
      {!isEditable && isOwner && (
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/15"></div>
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-yellow-100/80 flex items-center justify-center">
            <LockClosedIcon className="w-3 h-3 text-yellow-700" />
          </div>
        </div>
      )}
          </div>
      </Link>

            {/* Three Dots Menu - Top Right */}
            <div className="absolute top-4 right-4 z-50">
              <div className="relative">
                <Menu as="div" className="relative">
                  <Menu.Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-0 border border-transparent hover:border-[var(--color-border)]"
                    style={{
                      color: 'var(--color-text)',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    data-testid={`track-tile-menu-${track.id}`}
                    aria-label="Track actions menu"
                  >
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </Menu.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
                    <Menu.Items
                      className="absolute right-0 top-11 w-40 rounded-lg shadow-lg border py-1 focus:outline-none focus:ring-0"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        zIndex: 99999
                      }}
                    >
                {/* Share/Collaborators - Only show for owners and editors */}
                {(isOwner || sharedRole === 'editor') && (
                  <Menu.Item>
                    {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                            navigate(`/tracks/${track.id}/collaborators`);
                          }}
                          className={`flex items-center w-full px-3 py-2 text-sm focus:outline-none focus:ring-0 ${active ? '' : ''}`}
                          style={{
                            color: 'var(--color-text)',
                            backgroundColor: active ? 'var(--color-hover)' : 'transparent'
                          }}
                          data-testid={`track-tile-share-${track.id}`}
                        >
                          <ShareIcon className="w-4 h-4 mr-2" />
                          {t('buttons.share')}
                        </button>
                    )}
                  </Menu.Item>
                )}

                {/* Delete/Uncollaborate */}
                {isOwner && onDelete && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          onDelete();
                        }}
                        className={`flex items-center w-full px-3 py-2 text-sm focus:outline-none focus:ring-0 ${active ? '' : ''}`}
                        style={{
                          color: '#ef4444', // red-500
                          backgroundColor: active ? 'var(--color-hover)' : 'transparent'
                        }}
                        data-testid={`track-tile-delete-${track.id}`}
                      >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        {t('buttons.remove')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                {!isOwner && onUncollaborate && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          onUncollaborate();
                        }}
                        className={`flex items-center w-full px-3 py-2 text-sm ${active ? '' : ''}`}
                        style={{
                          color: '#ea580c', // orange-600
                          backgroundColor: active ? 'var(--color-hover)' : 'transparent'
                        }}
                        data-testid={`track-tile-uncollab-${track.id}`}
                      >
                        <UserMinusIcon className="w-4 h-4 mr-2" />
                        {t('dashboard.uncollab')}
                      </button>
                    )}
                  </Menu.Item>
                )}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

    {/* Share Info Popup - Outside Link to prevent event bubbling */}
    {showShareInfo && (
      <ShareInfoPopup
        trackId={track.id}
        trackName={track.name}
        isOwner={isOwner}
        ownerUserId={ownerUserId}
        onClose={() => setShowShareInfo(false)}
        onUncollaborateFromShareInfo={onUncollaborate}
      />
    )}
    </div>
    </>
  );
};

export default TrackTile;
